import {HookClass} from "@dvrt/graphql-widgets";
import {
    IvrCallsLazyQueryHookResult,
    IvrCallsQuery,
    IvrNumberIdQueryHookResult,
    useIvrCallsLazyQuery,
    useIvrCallsQuery,
    useIvrNumberIdQuery,
} from "../queries.generated";

export type Call = IvrCallsQuery["callList"]["calls"][number];

export default class DataSource extends HookClass {
    protected ivrId!: number;

    protected numberIdQueryResult!: IvrNumberIdQueryHookResult;
    protected relatedCallsQuery!: IvrCallsLazyQueryHookResult[0];
    protected callsQueryResult: any; // Add type based on useIvrCallsQuery return type
    public calls!: Call[];

    useHooks(ivrId: number, filterValue: string) {
        this.ivrId = ivrId;

        [this.relatedCallsQuery] = useIvrCallsLazyQuery();

        this.numberIdQueryResult = useIvrNumberIdQuery({
            variables: {
                ivrId: this.ivrId,
            },
        });

        const numberId = this.numberId;

        // Store the query result to access loading state
        this.callsQueryResult = useIvrCallsQuery({
            variables: {
                numberId: numberId,
                filterValue: filterValue,
            },
            skip: !numberId, // Skip query until numberId is available
            fetchPolicy: "no-cache",
        });

        this.calls = this.callsQueryResult.data?.callList?.calls?.filter((call: Call) => call.direction === "i") ?? [];
    }

    get numberId(): number | null {
        return this.numberIdQueryResult.data?.ivrs.items[0]?.number.id ?? null;
    }

    get domainName(): string | null {
        return this.numberIdQueryResult.data?.ivrs.items[0]?.number.domain.name ?? null;
    }

    async fetchRelatedCalls(call: Call, domainId: number) {
        if (!call) return [];
        if (!call.recordingId) return [call];

        // You'll need to keep a lazy query for this method since it's called imperatively
        const {data} = await this.relatedCallsQuery({
            variables: {
                domainId: domainId,
                recordingId: call.recordingId,
            },
            fetchPolicy: "no-cache",
        });

        const filteredCalls = data?.callList?.calls?.filter(
            (call) => call.remoteNumberId == this.numberId || call.direction === "i",
        ) ?? [call];

        if (this.domainName) {
            const domainSuffix = `@${this.domainName}`;
            return filteredCalls.map((c) => ({
                ...c,
                remoteNumber: c.remoteNumber.endsWith(domainSuffix)
                    ? c.remoteNumber.slice(0, -domainSuffix.length)
                    : c.remoteNumber,
            }));
        }
        return filteredCalls;
    }

    get loading(): boolean {
        return this.numberIdQueryResult.loading || this.callsQueryResult.loading;
    }

    get numberIdError() {
        return this.numberIdQueryResult.error;
    }
}
